<template>
  <div>
    <v-row justify="center" class="mt-2">
      <v-col cols="10">
        <v-row>
          <v-col cols="12" md="6" align-self="center">
            <h2 class="text-left">
              Staff Setup
            </h2>

            <h4 class="text-left">
              Create or import staff for your institution
            </h4>
          </v-col>

          <v-spacer></v-spacer>

          <v-col cols="12" md="3">
            <h2>
              4/4 Steps
            </h2>

            <v-progress-linear
              height="15"
              :value="99"
            >
            </v-progress-linear>
          </v-col>
        </v-row>

        <v-row>
          <v-col>
            <v-card class="pa-3">
              <v-row>
                <v-col cols="12" md="8" align-self="center">
                  <h3 class="text-left">
                    Directly import multiple staff (excel file)
                  </h3>
                </v-col>

                <v-spacer></v-spacer>

                <v-col cols="12" md="2" align-self="center">
                  <v-btn
                    color="primary"
                    @click="showImportDialog = true"
                    :disabled="isLoading || redirecting"
                  >
                    Import
                  </v-btn>
                </v-col>
              </v-row>
            </v-card>
          </v-col>
        </v-row>

        <v-row class="mb-1">
          <v-col>
            <v-card class="pa-3">
              <v-row class="mb-2">
                <v-col cols="12" md="8" align-self="center">
                  <h3 class="text-left">
                    Create Staff
                  </h3>
                </v-col>
              </v-row>
              
              <v-row justify="center">
                <v-col align-self="center">
                  <div class="text-left mb-2 mx-3">
                    <h3 class="font-weight-medium">Note:</h3>
                    <ul class="">
                      <li>All fields are 
                          <span class="text-decoration-underline">required</span>
                          and must be filled.
                      </li>
                      <li>No rows can be left blank.</li>
                      <li>Staff data must not be repeated.</li>
                      <li>The 
                          <span class="text-decoration-underline">password</span>
                          for each staff account will be their 
                          <span class="text-decoration-underline">phone number</span>.
                      </li>
                      <li>Cross-verify the entered data before proceeding.</li>
                    </ul>
                  </div>
                </v-col>
              </v-row>

              <v-form
                ref="staffDataForm"
                v-model="staffDataForm"
                @submit.prevent
                class="mt-1"
              >
                <v-container fluid class="mt-2">
                  <!-- hardcoded info of the admin/staff -->
                  <v-row 
                    no-gutters
                    justify="center"
                  >
                    <v-col cols="12" md="2" sm="6" align-self="center" >
                      <v-text-field
                        outlined
                        dense
                        class="rounded-input mx-2"
                        label="Full Name"
                        :rules="[rules.required]"
                        maxlength="200"
                        disabled
                        :value="currentUser.full_name"
                      ></v-text-field>
                    </v-col>

                    <v-col cols="12" md="2" sm="6" align-self="center">
                      <v-text-field
                        outlined
                        dense
                        class="rounded-input mx-2"
                        label="Phone"
                        :rules="[rules.required, rules.numbersOnly, rules.phoneNumberLength]"
                        maxlength="10"
                        :value="currentUser.phone"
                        disabled
                      ></v-text-field>
                    </v-col>

                    <v-col cols="12" md="2" sm="4" align-self="center">
                      <v-text-field
                        outlined
                        dense
                        class="rounded-input mx-2 message-chip"
                        label="Username"
                        :rules="[rules.required, rules.usernameValidation]"
                        maxlength="50"
                        :value="currentUser.username"
                        disabled
                      >
                      </v-text-field>
                    </v-col>

                    <v-col cols="12" md="3" sm="4">
                      <v-text-field
                        label="Email"
                        outlined
                        dense
                        class="rounded-input mx-2"
                        :rules="[rules.required, rules.emailFormat]"
                        maxlength="250"
                        :value="currentUser.email"
                        disabled
                      ></v-text-field>
                    </v-col>

                    <v-col cols="12" md="2" sm="2">
                      <v-checkbox
                        v-model="currentUser.is_admin"
                        label="Admin"
                        color="primary"
                        class="admin-checkbox  mt-1"
                        hide-details
                        disabled
                      ></v-checkbox>
                    </v-col>
                    
                    <v-col cols="12" md="1" sm="2">
                    </v-col>
                  </v-row>

                  <!-- staff data inputs -->
                  <v-row
                    no-gutters
                    justify="center"
                    v-for="(staff, i) in staffData"
                    :key="i"
                  >
                    <v-col cols="12" md="2" sm="6" align-self="center" >
                      <v-text-field
                        outlined
                        dense
                        class="rounded-input mx-2"
                        label="Full Name"
                        :rules="[rules.required]"
                        v-model="staff.full_name"
                        maxlength="200"
                      ></v-text-field>
                    </v-col>

                    <v-col cols="12" md="2" sm="6" align-self="center">
                      <v-text-field
                        outlined
                        dense
                        class="rounded-input mx-2"
                        label="Phone"
                        :rules="[rules.required, rules.numbersOnly, rules.phoneNumberLength]"
                        v-model="staff.phone"
                        maxlength="10"
                        counter
                      ></v-text-field>
                    </v-col>

                    <v-col cols="12" md="2" sm="4" align-self="center">
                      <v-text-field
                        outlined
                        dense
                        class="rounded-input mx-2 message-chip"
                        label="Username"
                        :rules="[rules.required, rules.usernameValidation]"
                        v-model="staff.username"
                        :messages="staff.phone && !staff.username ? message : null"
                        :error-messages="errors.username.length && errors.username[i] ? errors.username[i].error : null"
                        @change="checkUsernameExists(staff, i)"
                        maxlength="50"
                      >
                        <!-- eslint-disable-next-line vue/no-unused-vars -->
                        <template v-slot:message="{ message, key }">
                          <v-chip 
                            x-small
                            @click="copyFromPhone(staff, i)"
                            v-if="staff.phone && !staff.username && message=='Copy from Phone'"
                            outlined
                          >
                            {{ message }} 
                          </v-chip>
                        </template>
                      </v-text-field>
                    </v-col>

                    <v-col cols="12" md="3" sm="4">
                      <v-text-field
                        v-model="staff.email"
                        label="Email"
                        outlined
                        dense
                        class="rounded-input mx-2"
                        :rules="[rules.required, rules.emailFormat]"
                        maxlength="250"
                      ></v-text-field>
                    </v-col>

                    <v-col cols="12" md="2" sm="2">
                      <v-checkbox
                        v-model="staff.admin"
                        label="Admin"
                        color="primary"
                        class="admin-checkbox  mt-1"
                        hide-details
                      ></v-checkbox>
                    </v-col>
                    
                    <v-col cols="12" md="1" sm="2">
                      <v-icon
                        color="red"
                        @click="deleteStaff(i)"
                        class="mt-2 mx-1"
                      >
                        <!-- v-if="i != 0" -->
                        mdi-delete
                      </v-icon>
                    </v-col>
                  </v-row>

                  <v-row justify="center">
                    <v-col cols="12" md="4" align-self="center">
                      <v-btn
                        color="primary"
                        text
                        block
                        class="text-decoration-underline font-weight-medium"
                        @click="addStaff"
                      >
                        + Add another staff
                      </v-btn>
                    </v-col>
                  </v-row>

                  <v-row justify="center" class="my-1">
                    <v-col cols="12" md="4" sm="6" align-self="center">
                      <v-checkbox
                        label="Notify all staff?"
                        color="primary"
                        class="notify-staff mx-2 mt-1"
                        hide-details
                        v-model="notifyAll"
                      ></v-checkbox>
                    </v-col>
                  </v-row>

                  <v-row justify="center">
                    <v-col align-self="center">
                      <v-btn
                        color="primary"
                        :disabled="isLoading || !staffDataForm || redirecting"
                        @click="createStaffRecords"
                      >
                        Save & Proceed
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-container>
              </v-form>
            </v-card>
          </v-col>
        </v-row>

        <v-row justify="center" class="my-1">
          <v-col align-self="center">
            <SetupVideoDemo
              title="Watch a complete setup video!"
              description="Learn how to setup an institution on Intract with just a few steps by watching this video"
              url="https://www.youtube.com/watch?v=UuTAWC1bOqM"
            />
          </v-col>
        </v-row>

        <v-row class="mb-1">
          <v-col>
            <v-card class="pa-3">
              <v-row>
                <v-col cols="12" md="8" align-self="center">
                  <h3 class="text-left">
                    Get in touch with our onboarding manager
                  </h3>
                </v-col>

                <v-spacer></v-spacer>
                
                <v-col cols="12" md="3" align-self="center">
                  <v-btn
                    color="primary"
                    @click="showSupportDialog = true"
                  >
                    Contact Support
                  </v-btn>
                </v-col>
              </v-row>
            </v-card>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <!-- import excel dialog -->
    <ImportExcelDialog
			title="Import Staff"
			:showDialog="showImportDialog"
      :endpoint="endpoints.importExcelStaff"
			:staticFile="endpoints.static.staffImportFile"
			@closeImportDialog="closeImportDialog"
      @successfullyImported="successfullyImportedCallback"
		/>

    <SupportOptionsDialog
      :visible="showSupportDialog"
      @closeSupportOptionsDialog="closeSupportDialog"
    />

    <!-- snackbar -->
		<v-snackbar v-model="snackbar.visible" :color="snackbar.color" multi-line>
			{{ snackbar.message }}
			<template v-slot:action="{ attrs }">
				<v-btn dark text v-bind="attrs" @click="snackbar.visible = false">
					Close
				</v-btn>
			</template>
		</v-snackbar>
  </div>
</template>

<script>
import api from "@api/index"
// import authApi from "@api/auth/base"
import { mapActions, mapGetters } from "vuex"
import endpoints from "@api/repository"
// import Helper from "@utils/misc"
import moment from "moment"
import SupportOptionsDialog from "@components/widgets/dialogs/SupportOptionsDialog.vue"
import SetupVideoDemo from "@components/onboarding/SetupVideoDemo.vue"
import ImportExcelDialog from "@components/widgets/dialogs/ImportExcelDialog.vue"

export default {
  name: 'StaffSetup',

  components: {
    SupportOptionsDialog,
    SetupVideoDemo,
    ImportExcelDialog
  },

  props: {},

  computed: {
    ...mapGetters([
			"currentUser",
			"accessToken",
			"isLoading",
			"currentInstitution",
		]),

		apiEssentials() {
			return {
				accessToken: this.accessToken,
				setLoading: this.setLoading,
				handleErrorsFunction: this.handleApiErrors,
			};
		},
  },

  data() {
    return {
      moment: moment,
			snackbar: {
				visible: false,
				message: "",
				color: "dark",
			},

			rules: {
        required: (value) => !!value || "Required",
        numbersOnly: (v) => !v || !/[^0-9]/.test(v) || "Invalid. Value must be a number",
        usernameValidation: (v) => !v || !/[^a-zA-Z0-9.]/.test(v) || "Invalid",
        phoneNumberLength: (v) => !v || v?.length == 10 || "Should be 10 Digits",
        // eslint-disable-next-line no-useless-escape
        emailFormat: (v) => !v || /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/.test(v) || "Invalid email format. Format must be abc@def.gh",
      },

      errors: {
        username: [],
      },

      showImportDialog: false,
      showSupportDialog: false,

      endpoints: endpoints,
      staffDataForm: false,

      staffData: [
        {
          full_name: '',
          phone: '',
          username: '',
          admin: false,
          email: '',
        },
      ],

      notifyAll: false,

      message: 'Copy from Phone',

      redirecting: false,
    }
  },

  methods: {
    ...mapActions(
      [
        "setLoading",
        "setTokens",
        "setCurrentUser",
        "setCurrentInstitution",
        "setFeeSessionData",
        "clearTokens"
      ]
    ),

    async handleApiErrors(err) {
      this.setLoading(false);
      console.log(err);
      if (err.response) {
        if (err.response.status == 409) {
          this.snackbar.message = err.response.data;
          this.snackbar.color = "error";
          this.snackbar.visible = true;
        }
      } else {
        this.snackbar.message = "Unable to process request";
        this.snackbar.color = "error";
        this.snackbar.visible = true;
      }
    },

    async updateCurrentInstitutionDetails() {
      if(this.currentInstitution){
        var response;
        response = await api.call(this.apiEssentials, 
                                endpoints.getInstitutionDetailsById + (this.currentInstitution ? this.currentInstitution.id : 0 )+
                                "/",
                              );

        if (response != null) {
          this.setCurrentInstitution(response);
        }
      }
    },

    addStaff() {
      this.staffData.push({ full_name: '', phone: '', username: '', admin: false })
    },

    deleteStaff(index) {
      this.errors.username[index] = null
      this.staffData.splice(index, 1)
    },

    copyFromPhone(staff, i) {
      staff.username = staff.phone
      this.checkUsernameExists(staff, i)
    },

    async checkUsernameExists(staff, i){
      var url = endpoints.checkUsernameExists + `?username=${staff.username}`
      
      var usernameValid = true
      var count = 0
      // frontend check
      this.staffData.forEach(s => {
        if(s.username == staff.username) {
          count++
        }
      })
      
      if(count > 1) {
        usernameValid = false
      }

      // backend check
      if(await api.call(this.apiEssentials, url) || !usernameValid) {
        var errObj = { error: 'Username already exists' }
        this.$set(this.errors.username, i, errObj)
      }
      else {
        this.$set(this.errors.username, i, null)
      }
    },

    async createStaffRecords() {
      if(this.staffData.length) {

        if(!this.$refs.staffDataForm.validate()) {
          return
        }

        // trim classroom values
        this.staffData.map(staff => {
          Object.keys(staff).forEach(key => {
            if(typeof(staff[key]) == 'string') {
              staff[key] = staff[key].trim()
            }
            
            return staff[key]
          })
        })

        var url = endpoints.createStaffRecords
        var response = await api.call(this.apiEssentials, url, api.Methods.POST, { staff: this.staffData, notify_all: this.notifyAll })

        if(response) {
          this.updateCurrentInstitutionDetails()
          this.redirecting = true

          this.snackbar.message = "Staff Record(s) created successfully! Setup complete. REDIRECTING..."
          this.snackbar.visible = true
          this.snackbar.color = "success"

          setTimeout(() => {
            window.location.href= '/'
          }, 2000)
        }
      }
    },

    // emit from import dialog to indicate successful import
    successfullyImportedCallback(status) {
      if(status) {
        this.updateCurrentInstitutionDetails()
        this.redirecting = true
        
        this.snackbar.message = "Staff Records imported successfully! Setup complete. REDIRECTING..."
        this.snackbar.visible = true
        this.snackbar.color = "success"

        var self = this
        setTimeout(() => {
          self.$router.push({ name: 'Dashboard' })
        }, 2000)
      }
    },

    // receive dialog status from the component
		closeImportDialog(showDialog) {
			this.showImportDialog = showDialog
		},
    
    closeSupportDialog() {
      this.showSupportDialog = false
    }
  },

  created() {
    if(this.currentInstitution) {
      if(this.currentInstitution.stage == 0) {
        this.$router.push({ name: 'Dashboard' })
      }
      else if(this.currentInstitution.stage == 1) {
        this.$router.push({ name: 'ModulesSetup' })
      }
      else if(this.currentInstitution.stage == 2) {
        this.$router.push({ name: 'ClassroomSetup' })
      }
      else if(this.currentInstitution.stage == 3) {
        this.$router.push({ name: 'StudentsSetup' })
      }
    }
    else {
      this.$router.push({ name: 'LoginInstitute' })
    }
  }
}
</script>

<style>
  .message-chip .v-messages__message {
    text-align: center;
  }

  .admin-checkbox .v-input__slot {
    /* To center the switch element */
    justify-content: center !important;
  }

  .admin-checkbox .v-label {
    /* To remove the switch label padding */
    display: inline !important;
    position: relative !important;
    flex: none !important;
    width: 40%;
    white-space: nowrap;
  }

  .notify-staff .v-input__slot {
    /* To center the switch element */
    justify-content: center !important;
  }

  .notify-staff .v-label {
    /* To remove the switch label padding */
    display: inline !important;
    position: relative !important;
    flex: none !important;
    max-width: 70%;
  }
</style>